// @flow
import React, { type Node } from 'react';

type Props = {|
  allowClickAndDrag?: boolean,
  allowScroll?: boolean,
  children: Node,
|};

const noop = () => {};
const stopPropagation = (event: SyntheticEvent<>) => {
  event.stopPropagation();
  // We need to make sure that all the native events are also not propagated - see BUG-77298
  if (event.nativeEvent && event.nativeEvent.stopImmediatePropagation) {
    event.nativeEvent.stopImmediatePropagation();
  }
};

// Stop propagation up to parents https://github.com/facebook/react/issues/11387
export default function StopEventPropagation({ allowClickAndDrag, allowScroll, children }: Props) {
  return (
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    <div
      onCompositionEnd={stopPropagation}
      onCompositionStart={stopPropagation}
      onCompositionUpdate={stopPropagation}
      onCopy={stopPropagation}
      onCut={stopPropagation}
      onPaste={stopPropagation}
      onKeyDown={stopPropagation}
      onKeyPress={stopPropagation}
      onKeyUp={stopPropagation}
      onFocus={noop} // see BUG-78260
      onBlur={noop} // see BUG-78260
      onChange={stopPropagation}
      onInput={stopPropagation}
      onInvalid={stopPropagation}
      onSubmit={stopPropagation}
      onClick={allowClickAndDrag ? noop : stopPropagation}
      onContextMenu={stopPropagation}
      onDoubleClick={stopPropagation}
      onMouseDown={stopPropagation}
      onMouseEnter={stopPropagation}
      onMouseLeave={stopPropagation}
      onMouseMove={allowClickAndDrag ? noop : stopPropagation}
      onMouseOut={stopPropagation}
      onMouseOver={stopPropagation}
      onMouseUp={allowClickAndDrag ? noop : stopPropagation}
      onSelect={stopPropagation}
      onTouchCancel={stopPropagation}
      onTouchEnd={stopPropagation}
      onTouchMove={stopPropagation}
      onTouchStart={stopPropagation}
      onScroll={allowScroll ? noop : stopPropagation}
      onWheel={stopPropagation}
      onAbort={stopPropagation}
      onCanPlay={stopPropagation}
      onCanPlayThrough={stopPropagation}
      onDurationChange={stopPropagation}
      onEmptied={stopPropagation}
      onEncrypted={stopPropagation}
      onEnded={stopPropagation}
      onError={stopPropagation}
      onLoadedData={stopPropagation}
      onLoadedMetadata={stopPropagation}
      onLoadStart={stopPropagation}
      onPause={stopPropagation}
      onPlay={stopPropagation}
      onPlaying={stopPropagation}
      onProgress={stopPropagation}
      onRateChange={stopPropagation}
      onSeeked={stopPropagation}
      onSeeking={stopPropagation}
      onStalled={stopPropagation}
      onSuspend={stopPropagation}
      onTimeUpdate={stopPropagation}
      onVolumeChange={stopPropagation}
      onWaiting={stopPropagation}
      onLoad={stopPropagation}
      onAnimationStart={stopPropagation}
      onAnimationEnd={stopPropagation}
      onAnimationIteration={stopPropagation}
      onTransitionEnd={stopPropagation}
      /* eslint-enable jsx-a11y/no-static-element-interactions */
    >
      {children}
    </div>
  );
}
